/**
 * One error popup for every dashboard error (PjscDashboardApp card-decline popup; PjscAntiFraud #202610181112_7, #202610181112_8,
 * #202610181235_23): the reply's generalized text (a card decline: the fixed decline text), a support mailto link whose subject and body
 * carry the diagnostics, and those diagnostics as copyable text for a "Technical details" section collapsed by default. The popup
 * shows only what the server reply carries (message, reference, diagnostics), never a raw error dump.
 */

export const DECLINE_TITLE = "Card declined";
export const SUPPORT_EMAIL = "Support@PhantomJsCloud.com";
/** shown when no server reply is available (network failure, client-side error) */
export const GENERIC_ERROR_TEXT = "Something went wrong. Please try again in a few minutes.";

export interface ErrorPopupContent {
	title: string;
	message: string;
	mailtoHref: string;
	technicalDetails: string;
}

interface FoundReply {
	status: number;
	reply: { message?: string; reference?: string; diagnostics?: { [ field: string ]: any } };
}

/**
 * the server reply among the details the error flows pass to their popups: an axios error (reply under `response`) or a plain reply
 * body (`statusCode` + `message`, e.g. what the auto-add EzEndpoint intercept keeps as `data`). Other Error objects never match.
 */
export function findReply( ...errorDetails: any[] ): FoundReply | null {
	for ( const detail of errorDetails ) {
		if ( detail == null || typeof detail !== "object" ) {
			continue;
		}
		const isAxiosError = detail.response != null;
		if ( !isAxiosError && detail instanceof Error ) {
			continue;
		}
		const reply = isAxiosError ? detail.response.data : detail;
		const status = isAxiosError ? detail.response.status : detail.statusCode;
		if ( reply == null || typeof reply !== "object" || typeof reply.message !== "string" || reply.message.trim() === "" || typeof status !== "number" ) {
			continue;
		}
		return { status, reply };
	}
	return null;
}

/** `key: value` lines of the diagnostics, in the server's order */
function diagnosticsText( diagnostics: { [ field: string ]: any }, status: number ): string {
	const lines: string[] = [];
	for ( const key of Object.keys( diagnostics || {} ) ) {
		const value = diagnostics[ key ];
		if ( value != null && value !== "" ) {
			lines.push( `${ key }: ${ value }` );
		}
	}
	if ( status != null ) {
		lines.push( `status: ${ status }` );
	}
	return lines.join( "\n" );
}

/** the website's mailto convention (src/apps/pjsc-www/content/index.html): subject + body prefilled, body carries the diagnostics */
export function supportMailto( title: string, reference: string, technicalDetails: string ): string {
	const subject = `Please help with my PhantomJsCloud.com problem: ${ title }${ reference ? ` (ref ${ reference })` : "" }`;
	const body = `Details of my problem: ...\n\nTechnical details:\n${ technicalDetails }`;
	return `mailto:${ SUPPORT_EMAIL }?subject=${ encodeURIComponent( subject ) }&body=${ encodeURIComponent( body ) }`;
}

/**
 * popup content for the details an error flow passes (`fallbackTitle` names the flow, e.g. "Error making purchase"):
 * a server reply → its message, reference and diagnostics (402 → title "Card declined"); otherwise an app-authored text argument
 * (a string not ending in ":") or the generic text, with the time and the page action as details
 */
export function errorPopupContent( fallbackTitle: string, ...errorDetails: any[] ): ErrorPopupContent {
	const found = findReply( ...errorDetails );
	if ( found != null ) {
		const title = found.status === 402 ? DECLINE_TITLE : fallbackTitle;
		const technicalDetails = diagnosticsText( found.reply.diagnostics, found.status );
		return { title, message: found.reply.message.trim(), mailtoHref: supportMailto( title, found.reply.reference, technicalDetails ), technicalDetails };
	}
	const text = errorDetails.find( ( d ) => typeof d === "string" && d.trim() !== "" && !/:\s*$/.test( d ) );
	const technicalDetails = [ `time: ${ new Date().toISOString() }`, `action: ${ fallbackTitle }` ].join( "\n" );
	return { title: fallbackTitle, message: text != null ? text : GENERIC_ERROR_TEXT, mailtoHref: supportMailto( fallbackTitle, null, technicalDetails ), technicalDetails };
}
